<template>
  <div class="grid-container" id="header-grid" ref="screenContent">
    <div class="item">
      <h1>Order {{ formdata.orderId }} has been submitted</h1>
      <div class="hidden-print-only">
        <v-alert
          text
          color="error"
          type="error"
          class="top-spaced-item multi-line custom-alert mt-10"
          outlined
          v-if="checkToDisplayPathologyAlert()"
        >
          <b>Important:</b> Please download the pathology collection forms now.
          Forms will be inaccessible upon leaving this page or closing the
          browser.
        </v-alert>
      </div>
      <div class="mt-6 hidden-print-only">
        <v-btn
          @click="downloadAllRelevantForms()"
          class="primary-btn btn-padding-large"
          :style="{ 'min-width': '250px' }"
        >
          Download the order summary/pathology collection forms
        </v-btn>
      </div>

      <div class="pt-6 buttons-container hidden-print-only">
        <v-btn
          @click="areAllRequiredPDFsDownloaded() ? reloadPage() : showDialog()"
          class="secondary-btn btn-padding-large"
          max-width="250"
          >Order another test
        </v-btn>

        <v-btn
          large
          @click="
            areAllRequiredPDFsDownloaded()
              ? orderForFamilyMember()
              : showDialog()
          "
          class="secondary-btn btn-padding-large left-spaced-item"
          max-width="250"
          >Order for family member
        </v-btn>
      </div>

      <div class="hidden-print-only">Thank you for your order. <br /></div>
      <span
        v-if="
          dataClinexTest.billTo === 'MEDICARE' && !dataClinexTest.cgApproved
        "
      >
        A VCGS clinical geneticist will be in touch to discuss this request to
        ensure Medicare eligibility and suggest suitable gene panels.<br />
      </span>
      <span v-if="checkForAnySampleType('SALIVA')">
        We will arrange for saliva kits to be sent to the patient/s. Upon kit
        dispatch, we will send a confirmation email to the patient/s and
        yourself.<br />
      </span>

      <div class="hidden-print-only mt-4">
        We've also
        <a
          id="bookmarkGPLink"
          :href="generateGPBookmark(formdata.practitioner)"
          target="_blank"
          >saved your practitioner details</a
        >. Save this link or bookmark it for future orders.
      </div>

      <h2 class="mt-8 mb-2">Order details</h2>

      <table data-testid="completed-practitioner-table">
        <tr>
          <td>ORDER DATE</td>
          <td>{{ formdata.dateSubmitted }}</td>
        </tr>
        <tr>
          <td>PRACTITIONER NAME</td>
          <td>
            {{ keyLookup("titles", formdata.practitioner.namePrefix) }}
            {{ formdata.practitioner.firstName }}
            {{ formdata.practitioner.lastName }}
          </td>
        </tr>
        <tr>
          <td>PROVIDER NO.</td>
          <td>{{ formdata.practitioner.providerNum }}</td>
        </tr>
        <tr>
          <td>CLINIC NAME</td>
          <td>{{ formdata.practitioner.clinicName }}</td>
        </tr>
        <tr>
          <td style="vertical-align: top">ADDRESS</td>
          <td>
            {{ formdata.practitioner.unitFlat }}
            {{ formdata.practitioner.dept }}
            <br />
            {{ formdata.practitioner.streetAddress }}
            <br />
            {{ formdata.practitioner.city }}
            {{ formdata.practitioner.state }}
            {{ formdata.practitioner.postcode }}
            {{ keyLookup("countries", formdata.practitioner.country) }}
          </td>
        </tr>
        <tr>
          <td>EMAIL ADDRESS</td>
          <td>{{ formdata.practitioner.emailAddress }}</td>
        </tr>
        <tr>
          <td>MOBILE/PHONE NUMBER</td>
          <td>{{ formdata.practitioner.number }}</td>
        </tr>
        <tr>
          <td>FAX NUMBER</td>
          <td>{{ formdata.practitioner.faxNum }}</td>
        </tr>
      </table>

      <div v-if="formdata.practitioner.copyToDoctors.length > 0">
        <h2 class="mt-8 mb-2">Copy to</h2>
        <table
          class="full-width-table"
          v-for="(doc, idx) in formdata.practitioner.copyToDoctors"
          :key="idx"
          data-testid="completed-copyToDoctors-table"
        >
          <tr>
            <td>{{ doc.providerNum }}</td>
            <td>{{ doc.firstName }} {{ doc.lastName }} {{ doc.clinicName }}</td>
            <td>{{ doc.emailAddress }}</td>
          </tr>
        </table>
      </div>
      <h2 class="mt-8 mb-2">Patient details</h2>
      <table data-testid="completed-patient-table">
        <tr>
          <td>PATIENT ID</td>
          <td>
            {{ formdata.patient.externalId }}
          </td>
        </tr>
        <tr v-if="store.isPrenatal">
          <td>GESTATION LENGTH</td>
          <td>{{ formdata.patient.gestationLength }}</td>
        </tr>
        <tr>
          <td>PATIENT NAME</td>
          <td>
            {{ formdata.patient.firstName }}
            {{ formdata.patient.lastName }}
          </td>
        </tr>
        <tr>
          <td>DATE OF BIRTH</td>
          <td>
            {{ reformattedPatientDob }}
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>SEX</td>
          <td>
            {{ keyLookup("sexes", formdata.patient.sex) }}
          </td>
        </tr>
        <tr v-if="store.hasPregnantTests">
          <td>PREGNANT</td>
          <td>{{ pregnant === "YES" ? "Yes" : "No" }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>ETHNICITY</td>
          <td>
            {{ keyLookup("ethnicities", formdata.patient.ethnicity) }}
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>SAMPLE TYPE</td>
          <td>
            {{ sampleTypeName(formdata.patient.sampleType, false) }}
          </td>
        </tr>
        <tr
          v-if="
            formdata.patient.sampleType === 'STORED_DNA' ||
              formdata.patient.sampleType === 'STORED_DATA' ||
              formdata.patient.sampleType === 'NON_VCGS_SAMPLE'
          "
        >
          <td>SAMPLE ID</td>
          <td>
            {{
              formdata.patient.sampleType === "STORED_DNA" ||
              formdata.patient.sampleType === "STORED_DATA"
                ? formdata.patient.storedSampleId
                : formdata.patient.nonVCGSSampleID
            }}
          </td>
        </tr>
        <tr>
          <td style="vertical-align: top">ADDRESS</td>

          <td>
            {{ formdata.patient.unitFlat }}
            {{ formdata.patient.streetAddress }}
            <br />
            {{ formdata.patient.city }}
            {{ formdata.patient.state }}
            {{ formdata.patient.postcode }}
            {{ keyLookup("countries", formdata.patient.country) }}
          </td>
        </tr>
        <tr>
          <td>EMAIL ADDRESS</td>
          <td>{{ formdata.patient.emailAddress }}</td>
        </tr>
        <tr>
          <td>MOBILE NUMBER</td>
          <td>{{ formdata.patient.mobileNum }}</td>
        </tr>
        <tr>
          <td>MEDICARE NUMBER & POSITION</td>
          <td>
            {{ formdata.patient.medicareNum }} /
            {{ formdata.patient.mediPersonNum }}
          </td>
        </tr>
      </table>

      <h2 class="mt-8 mb-2">Test details</h2>
      <p>
        Your doctor has recommended you use Victorian Clinical Genetics Services
        (VCGS). You are free to choose your own pathology provider. However, if
        your doctor has specified a particular pathologist on clinical grounds,
        a Medicare rebate will only be payable if that pathologist performed the
        service. You should discuss this with your doctor.
      </p>
      <table v-if="showFragileXTest" data-testid="completed-fragilex-table">
        <tr>
          <td><h3>Fragile X</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>URGENT KIT REQUIRED</td>
          <td>{{ yesNoNull(dataFragileXTest.urgent) }}</td>
        </tr>
        <tr>
          <td>URGENT KIT REASON</td>
          <td>{{ dataFragileXTest.urgentReason }}</td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataFragileXTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataFragileXTest.quotedPrice }}</td>
        </tr>
      </table>

      <table v-if="showPraderWilli" data-testid="completed-praderwilli-table">
        <tr>
          <td><h3>Prader Willi / Angelman Syndrome</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>URGENT KIT REQUIRED</td>
          <td>{{ yesNoNull(dataPraderWilli.urgent) }}</td>
        </tr>
        <tr>
          <td>URGENT KIT REASON</td>
          <td>{{ dataPraderWilli.urgentReason }}</td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataPraderWilli.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataPraderWilli.quotedPrice }}</td>
        </tr>
      </table>

      <table v-for="(test, key) in mediumTestData" :key="key">
        <table v-if="test.show">
          <tr>
            <td>
              <h3>
                {{ test.testName }}
              </h3>
            </td>
            <td></td>
          </tr>
          <tr v-if="test.data.testType !== 'deafness_mlpa'">
            <td>PREDICTIVE TEST?</td>
            <td>{{ yesNoNull(test.data.predictiveTest) }}</td>
          </tr>
          <tr></tr>
          <tr>
            <td>FAMILY MEMBER PREVIOUSLY TESTED?</td>
            <td>{{ yesNoNull(test.data.familyPrevTested) }}</td>
          </tr>
          <tr v-if="test.data.familyPrevTested">
            <td>FAMILY MEMBER NAME</td>
            <td>{{ yesNoNull(test.data.posCtrlName) }}</td>
          </tr>
          <tr v-if="test.data.familyPrevTested">
            <td>FAMILY MEMBER DATE OF BIRTH</td>
            <td>{{ reformatDob(yesNoNull(test.data.posCtrlBirthDate)) }}</td>
          </tr>
          <tr>
            <td>VCGS SAMPLE ID</td>
            <td>{{ test.data.posCtrlSampleId }}</td>
          </tr>
          <tr>
            <td>RELATION AFFECTED?</td>
            <td>{{ test.data.relationAffected }}</td>
          </tr>
          <tr>
            <td>RELATION TO POSITIVE CONTROL</td>
            <td>{{ test.data.rhipToPosCtrl }}</td>
          </tr>
          <tr>
            <td>CLINICAL NOTES</td>
            <td>{{ test.data.clinicalNotes }}</td>
          </tr>
          <tr>
            <td>PRICE</td>
            <td>{{ test.data.quotedPrice }}</td>
          </tr>
        </table>
      </table>

      <table v-if="showConnexinTest" data-testid="completed-connexin-table">
        <tr>
          <td><h3>Connexin / DFNB1</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>TEST TYPE</td>
          <td>{{ dataConnexinTest.testCode }}</td>
        </tr>
        <tr
          v-if="dataConnexinTest.testCode === 'connexin_dfnb1_carrierTesting'"
        >
          <td>FAMILY MEMBER PREVIOUSLY TESTED?</td>
          <td>{{ yesNoNull(dataConnexinTest.familyPrevTested) }}</td>
        </tr>
        <tr
          v-if="dataConnexinTest.testCode === 'connexin_dnfb1_carrierTesting'"
        >
          <td>VCGS SAMPLE ID</td>
          <td>{{ dataConnexinTest.posCtrlSampleId }}</td>
        </tr>
        <tr
          v-if="dataConnexinTest.testCode === 'connexin_dnfb1_carrierTesting'"
        >
          <td>RELATION AFFECTED?</td>
          <td>
            {{ dataConnexinTest.relationAffected }}
          </td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataConnexinTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataConnexinTest.quotedPrice }}</td>
        </tr>
      </table>

      <table v-if="showMicroArrayTest" data-testid="completed-microarray-table">
        <tr>
          <td><h3>Microarray</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>URGENT KIT REQUIRED</td>
          <td>{{ yesNoNull(dataMicroArrayTest.urgent) }}</td>
        </tr>
        <tr>
          <td>URGENT KIT REASON</td>
          <td>{{ dataMicroArrayTest.urgentReason }}</td>
        </tr>
        <tr>
          <td>FAMILY MEMBER PREVIOUSLY TESTED?</td>
          <td>{{ yesNoNull(dataMicroArrayTest.familyPrevTested) }}</td>
        </tr>
        <tr>
          <td>VCGS SAMPLE ID</td>
          <td>{{ dataMicroArrayTest.posCtrlSampleId }}</td>
        </tr>
        <tr>
          <td>RELATION AFFECTED?</td>
          <td>
            {{ dataMicroArrayTest.relationAffected }}
          </td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataMicroArrayTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataMicroArrayTest.quotedPrice }}</td>
        </tr>
      </table>

      <table
        v-if="showPrenatalTargetedExome"
        data-testid="completed-prenatal-targeted-table"
      >
        <tr>
          <td><h3>Prenatal Targeted Exome (Familial Variant Testing)</h3></td>
          <td></td>
        </tr>
        <tr v-for="item in dataPrenatalTargetedExome" :key="item.key">
          <td>{{ item.key }}</td>
          <td>{{ item.value }}</td>
        </tr>
      </table>

      <div
        v-if="
          showPrenatalTargetedExome &&
            completedStoreData.prenatal_targeted_exome_family &&
            completedStoreData.prenatal_targeted_exome_family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in completedStoreData.prenatal_targeted_exome_family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>

      <table
        v-if="showPrenatalTargetedWGS"
        data-testid="completed-prenatal-targeted-wgs-table"
      >
        <tr>
          <td><h3>Prenatal Targeted WGS (Familial Variant Testing)</h3></td>
          <td></td>
        </tr>
        <tr v-for="item in dataPrenatalTargetedWGS" :key="item.key">
          <td>{{ item.key }}</td>
          <td>{{ item.value }}</td>
        </tr>
      </table>

      <div
        v-if="
          showPrenatalTargetedWGS &&
            completedStoreData.prenatal_targeted_genome_family &&
            completedStoreData.prenatal_targeted_genome_family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">
          Parent details
        </h2>
        <table
          class="full-width-table"
          v-for="person in completedStoreData.prenatal_targeted_genome_family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>

      <table
        v-if="showPrenatalRapidGenomicTrio"
        data-testid="completed-prenatal-rapid-trio-table"
      >
        <tr>
          <td><h3>Prenatal Rapid Genomic Trio</h3></td>
          <td></td>
        </tr>
        <tr v-for="item in dataPrenatalRapidGenomicTrio" :key="item.key">
          <td>{{ item.key }}</td>
          <td>{{ item.value }}</td>
        </tr>
      </table>

      <div
        v-if="
          showPrenatalRapidGenomicTrio &&
            completedStoreData.prenatal_rapid_genomic_trio_family &&
            completedStoreData.prenatal_rapid_genomic_trio_family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in completedStoreData.prenatal_rapid_genomic_trio_family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>

      <table v-if="showVariantDetection" data-testid="completed-variant-table">
        <tr>
          <td><h3>Familial Variant Detection</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>RAPID?</td>
          <td>{{ yesNoNull(dataVariantDetection.urgency) }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>PREDICTIVE TEST?</td>
          <td>{{ yesNoNull(dataVariantDetection.predictiveTest) }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY MEMBER PREVIOUSLY TESTED?</td>
          <td>{{ yesNoNull(dataVariantDetection.familyPrevTested) }}</td>
        </tr>
        <tr v-if="dataVariantDetection.familyPrevTested">
          <td>FAMILY MEMBER NAME</td>
          <td>{{ yesNoNull(dataVariantDetection.posCtrlName) }}</td>
        </tr>
        <tr v-if="dataVariantDetection.familyPrevTested">
          <td>FAMILY MEMBER DATE OF BIRTH</td>
          <td>
            {{ reformatDob(yesNoNull(dataVariantDetection.posCtrlBirthDate)) }}
          </td>
        </tr>
        <tr>
          <td>VCGS SAMPLE ID</td>
          <td>{{ dataVariantDetection.posCtrlSampleId }}</td>
        </tr>
        <tr>
          <td>RELATION AFFECTED?</td>
          <td>{{ dataVariantDetection.relationAffected }}</td>
        </tr>
        <tr>
          <td>RELATION TO POSITIVE CONTROL</td>
          <td>{{ dataVariantDetection.rhipToPosCtrl }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>NUMBER OF VARIANTS</td>
          <td>{{ dataVariantDetection.numVariants }}</td>
        </tr>
        <tr>
          <td>VARIANT DETAILS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(variantDetail,
                $index) in dataVariantDetection.variantDetails"
                :key="$index"
              >
                {{ variantDetail }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataVariantDetection.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataVariantDetection.quotedPrice }}</td>
        </tr>
      </table>

      <div
        v-if="
          dataVariantDetection.family && dataVariantDetection.family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataVariantDetection.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>

      <table v-if="showMitoTest" data-testid="completed-mito-table">
        <tr>
          <td><h3>Whole Mitochondrial Genome</h3></td>
          <td></td>
        </tr>
        <tr></tr>
        <tr>
          <td>RAPID?</td>
          <td>{{ yesNoNull(dataMitoTest.urgency) }}</td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataMitoTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataMitoTest.quotedPrice }}</td>
        </tr>
      </table>

      <table
        v-if="showMitoCascadeTest"
        data-testid="completed-mito-cascade-table"
      >
        <tr>
          <td><h3>Whole Mitochondrial Genome Cascade & Family</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>FAMILY MEMBER PREVIOUSLY TESTED?</td>
          <td>{{ yesNoNull(dataMitoCascadeTest.familyPrevTested) }}</td>
        </tr>
        <tr v-if="dataMitoCascadeTest.familyPrevTested">
          <td>FAMILY MEMBER NAME</td>
          <td>{{ yesNoNull(dataMitoCascadeTest.posCtrlName) }}</td>
        </tr>
        <tr v-if="dataMitoCascadeTest.familyPrevTested">
          <td>FAMILY MEMBER DATE OF BIRTH</td>
          <td>
            {{ reformatDob(yesNoNull(dataMitoCascadeTest.posCtrlBirthDate)) }}
          </td>
        </tr>
        <tr>
          <td>VCGS SAMPLE ID</td>
          <td>{{ dataMitoCascadeTest.posCtrlSampleId }}</td>
        </tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ yesNoNull(dataMitoCascadeTest.familyHx) }}</td>
        </tr>
        <tr>
          <td>RELATION AFFECTED?</td>
          <td>{{ dataMitoCascadeTest.relationAffected }}</td>
        </tr>
        <tr>
          <td>RELATION TO POSITIVE CONTROL</td>
          <td>{{ dataMitoCascadeTest.rhipToPosCtrl }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>NUMBER OF VARIANTS</td>
          <td>{{ dataMitoCascadeTest.numVariants }}</td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataMitoCascadeTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataMitoCascadeTest.quotedPrice }}</td>
        </tr>
      </table>

      <table
        v-if="showMitoDiseaseTest"
        data-testid="completed-mito-disease-table"
      >
        <tr>
          <td><h3>Mitochondrial disease - diagnostic WGS testing</h3></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ yesNoNull(dataMitoDiseaseTest.billTo) }}</td>
        </tr>
        <tr>
          <td>URGENT</td>
          <td>{{ yesNoNull(dataMitoDiseaseTest.urgent) }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ yesNoNull(dataMitoDiseaseTest.familyType) }}</td>
        </tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype,
                $index) in dataMitoDiseaseTest.phenotypes.split('|')"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataMitoDiseaseTest.clinicalNotes }}</td>
        </tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataMitoDiseaseTest.familyHx }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataMitoDiseaseTest.quotedPrice }}</td>
        </tr>
      </table>

      <div
        v-if="
          dataMitoDiseaseTest.family && dataMitoDiseaseTest.family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataMitoDiseaseTest.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showMitoDiseaseTest"
        data-testid="completed-mito-disease-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>{{ yesNoNull(dataMitoDiseaseTest.patientTestConsentAck) }}</td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>
            {{ yesNoNull(dataMitoDiseaseTest.patientResearchConsentAck) }}
          </td>
        </tr>
        <tr></tr>
        <table
          v-if="
            dataMitoDiseaseTest.family && dataMitoDiseaseTest.family.length > 0
          "
        >
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>{{ yesNoNull(dataMitoDiseaseTest.trioTestConsentAck) }}</td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataMitoDiseaseTest.motherResearchConsentAck) }}
            </td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataMitoDiseaseTest.fatherResearchConsentAck) }}
            </td>
          </tr>
        </table>
      </table>

      <table
        v-if="showSensorineuralTest"
        data-testid="completed-sensorineural-table"
      >
        <tr>
          <td><h3>Exome Focused + Deafness MLPA Bundle</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataSensorineuralTest.billTo }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataSensorineuralTest.familyType }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataSensorineuralTest.analysisType }}</td>
        </tr>
        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel,
                $index) in dataSensorineuralTest.genePanels.split('|')"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype,
                $index) in dataSensorineuralTest.phenotypes.split('|')"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataSensorineuralTest.familyHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataSensorineuralTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataSensorineuralTest.quotedPrice }}</td>
        </tr>
      </table>

      <table v-if="showClinexTest" data-testid="completed-clinex-table">
        <tr>
          <td><h3>Clinical Exome</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataClinexTest.billTo }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>URGENT</td>
          <td>{{ yesNoNull(dataClinexTest.urgent) }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataClinexTest.familyType }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataClinexTest.analysisType }}</td>
        </tr>

        <tr v-if="dataClinexTest.billTo === 'MEDICARE'">
          <td>CLINICAL GENETICIST APPROVED</td>
          <td>{{ yesNoNull(dataClinexTest.cgApproved) }}</td>
        </tr>

        <tr v-if="dataClinexTest.billTo === 'MEDICARE'">
          <td>CLINICAL GENETICIST DETAILS</td>
          <td>{{ dataClinexTest.cgDetails }}</td>
        </tr>

        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel, $index) in dataClinexTest.genePanels.split('|')"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>

        <tr></tr>
        <tr>
          <td>INDIVIDUAL GENES</td>
          <td>{{ dataClinexTest.indGenes.replace(/\|/g, ", ") }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype, $index) in dataClinexTest.phenotypes.split(
                  '|',
                )"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>AGE OF ONSET</td>
          <td>{{ dataClinexTest.ageOfOnset }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataClinexTest.familyHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataClinexTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataClinexTest.quotedPrice }}</td>
        </tr>
      </table>

      <div v-if="dataClinexTest.family && dataClinexTest.family.length > 0">
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataClinexTest.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showClinexTest"
        data-testid="completed-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>{{ yesNoNull(dataClinexTest.patientTestConsentAck) }}</td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>{{ yesNoNull(dataClinexTest.patientResearchConsentAck) }}</td>
        </tr>
        <tr></tr>
        <table v-if="dataClinexTest.family && dataClinexTest.family.length > 0">
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>{{ yesNoNull(dataClinexTest.trioTestConsentAck) }}</td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>{{ yesNoNull(dataClinexTest.motherResearchConsentAck) }}</td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>{{ yesNoNull(dataClinexTest.fatherResearchConsentAck) }}</td>
          </tr>
        </table>
      </table>

      <table
        v-if="showChildhoodSyndromes"
        data-testid="completed-childhood-syndromes-table"
      >
        <tr>
          <td><h3>Childhood syndrome - diagnostic exome or WGS</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataChildhoodSyndromes.billTo }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>URGENT</td>
          <td>{{ yesNoNull(dataChildhoodSyndromes.urgent) }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataChildhoodSyndromes.familyType }}</td>
        </tr>
        <tr v-if="dataChildhoodSyndromes.familyType === 'Singleton'">
          <td>BIOLOGICAL PARENTS AVAILABILITY</td>
          <td>{{ childhoodParentsAvail }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataChildhoodSyndromes.analysisType }}</td>
        </tr>

        <tr>
          <td>CLINICAL GENETICIST APPROVED</td>
          <td>{{ yesNoNull(dataChildhoodSyndromes.cgApproved) }}</td>
        </tr>

        <tr>
          <td>CLINICAL GENETICIST DETAILS</td>
          <td>{{ dataChildhoodSyndromes.cgDetails }}</td>
        </tr>

        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel,
                $index) in dataChildhoodSyndromes.genePanels.split('|')"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>

        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype,
                $index) in dataChildhoodSyndromes.phenotypes.split('|')"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>

        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ childhoodFamHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES & MICROARRAY RESULTS</td>
          <td>{{ dataChildhoodSyndromes.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataChildhoodSyndromes.quotedPrice }}</td>
        </tr>
      </table>

      <div
        v-if="
          dataChildhoodSyndromes.family &&
            dataChildhoodSyndromes.family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataChildhoodSyndromes.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showChildhoodSyndromes"
        data-testid="completed-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>{{ yesNoNull(dataChildhoodSyndromes.patientTestConsentAck) }}</td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>
            {{ yesNoNull(dataChildhoodSyndromes.patientResearchConsentAck) }}
          </td>
        </tr>
        <tr></tr>
        <table
          v-if="
            dataChildhoodSyndromes.family &&
              dataChildhoodSyndromes.family.length > 0
          "
        >
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>{{ yesNoNull(dataChildhoodSyndromes.trioTestConsentAck) }}</td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataChildhoodSyndromes.motherResearchConsentAck) }}
            </td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataChildhoodSyndromes.fatherResearchConsentAck) }}
            </td>
          </tr>
        </table>
      </table>

      <table
        v-if="showWholeGenomeSequencingTest"
        data-testid="completed-whole-genome-sequencing-table"
      >
        <tr>
          <td><h3>Whole genome sequencing</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataWholeGenomeSequencingTest.billTo }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>URGENT</td>
          <td>{{ yesNoNull(dataWholeGenomeSequencingTest.urgent) }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataWholeGenomeSequencingTest.familyType }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataWholeGenomeSequencingTest.analysisType }}</td>
        </tr>
        <tr
          v-if="
            dataWholeGenomeSequencingTest.analysisType ===
              'Comprehensive Genome'
          "
        >
          <td>INCLUDE MITO GENOME</td>
          <td>{{ yesNoNull(dataWholeGenomeSequencingTest.mtDna) }}</td>
        </tr>

        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel,
                $index) in dataWholeGenomeSequencingTest.genePanels.split('|')"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>

        <tr></tr>
        <tr>
          <td>INDIVIDUAL GENES</td>
          <td>
            {{ dataWholeGenomeSequencingTest.indGenes.replace(/\|/g, ", ") }}
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype,
                $index) in dataWholeGenomeSequencingTest.phenotypes.split('|')"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>AGE OF ONSET</td>
          <td>{{ dataWholeGenomeSequencingTest.ageOfOnset }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataWholeGenomeSequencingTest.familyHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataWholeGenomeSequencingTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataWholeGenomeSequencingTest.quotedPrice }}</td>
        </tr>
      </table>

      <div
        v-if="
          dataWholeGenomeSequencingTest.family &&
            dataWholeGenomeSequencingTest.family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataWholeGenomeSequencingTest.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showWholeGenomeSequencingTest"
        data-testid="completed-whole-genome-sequencing-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>
            {{ yesNoNull(dataWholeGenomeSequencingTest.patientTestConsentAck) }}
          </td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>
            {{
              yesNoNull(dataWholeGenomeSequencingTest.patientResearchConsentAck)
            }}
          </td>
        </tr>
        <tr></tr>
        <table
          v-if="
            dataWholeGenomeSequencingTest.family &&
              dataWholeGenomeSequencingTest.family.length > 0
          "
        >
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>
              {{ yesNoNull(dataWholeGenomeSequencingTest.trioTestConsentAck) }}
            </td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>
              {{
                yesNoNull(
                  dataWholeGenomeSequencingTest.motherResearchConsentAck,
                )
              }}
            </td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>
              {{
                yesNoNull(
                  dataWholeGenomeSequencingTest.fatherResearchConsentAck,
                )
              }}
            </td>
          </tr>
        </table>
      </table>

      <table v-if="showAlportTest" data-testid="completed-alport-table">
        <tr>
          <td><h3>Alport syndrome diagnostic testing</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataAlportTest.billTo }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataAlportTest.familyType }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataAlportTest.analysisType }}</td>
        </tr>
        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel, $index) in dataAlportTest.genePanels.split('|')"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype, $index) in dataAlportTest.phenotypes.split(
                  '|',
                )"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataAlportTest.familyHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataAlportTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataAlportTest.quotedPrice }}</td>
        </tr>
      </table>

      <div v-if="dataAlportTest.family && dataAlportTest.family.length > 0">
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataAlportTest.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showAlportTest"
        data-testid="completed-alport-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>{{ yesNoNull(dataAlportTest.patientTestConsentAck) }}</td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>{{ yesNoNull(dataAlportTest.patientResearchConsentAck) }}</td>
        </tr>
        <tr></tr>
        <table v-if="dataAlportTest.family && dataAlportTest.family.length > 0">
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>{{ yesNoNull(dataAlportTest.trioTestConsentAck) }}</td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>{{ yesNoNull(dataAlportTest.motherResearchConsentAck) }}</td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>{{ yesNoNull(dataAlportTest.fatherResearchConsentAck) }}</td>
          </tr>
        </table>
      </table>

      <table
        v-if="showRenalNonCysticTest"
        data-testid="completed-renal-non-cystic-table"
      >
        <tr>
          <td><h3>Heritable Kidney Disease (non-cystic, non-Alport)</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataRenalNonCysticTest.billTo }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataRenalNonCysticTest.familyType }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataRenalNonCysticTest.analysisType }}</td>
        </tr>
        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel,
                $index) in dataRenalNonCysticTest.genePanels.split('|')"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype,
                $index) in dataRenalNonCysticTest.phenotypes.split('|')"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataRenalNonCysticTest.familyHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataRenalNonCysticTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataRenalNonCysticTest.quotedPrice }}</td>
        </tr>
      </table>

      <div
        v-if="
          dataRenalNonCysticTest.family &&
            dataRenalNonCysticTest.family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataRenalNonCysticTest.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showRenalNonCysticTest"
        data-testid="completed-renal-non-cystic-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>{{ yesNoNull(dataRenalNonCysticTest.patientTestConsentAck) }}</td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>
            {{ yesNoNull(dataRenalNonCysticTest.patientResearchConsentAck) }}
          </td>
        </tr>
        <tr></tr>
        <table
          v-if="
            dataRenalNonCysticTest.family &&
              dataRenalNonCysticTest.family.length > 0
          "
        >
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>{{ yesNoNull(dataRenalNonCysticTest.trioTestConsentAck) }}</td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataRenalNonCysticTest.motherResearchConsentAck) }}
            </td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataRenalNonCysticTest.fatherResearchConsentAck) }}
            </td>
          </tr>
        </table>
      </table>

      <table
        v-if="showRenalCysticTest"
        data-testid="completed-renal-cystic-table"
      >
        <tr>
          <td><h3>Heritable Cystic Kidney Disease</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>BILLING TO</td>
          <td>{{ dataRenalCysticTest.billTo }}</td>
        </tr>
        <tr>
          <td>FAMILY TYPE</td>
          <td>{{ dataRenalCysticTest.familyType }}</td>
        </tr>
        <tr>
          <td>ANALYSIS TYPE</td>
          <td>{{ dataRenalCysticTest.analysisType }}</td>
        </tr>
        <tr>
          <td>GENE PANELS</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(panel, $index) in dataRenalCysticTest.genePanels.split(
                  '|',
                )"
                :key="$index"
              >
                {{ panel }}
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>CLINICAL FEATURES</td>
          <td>
            <ul class="no-style-list">
              <li
                v-for="(phenotype,
                $index) in dataRenalCysticTest.phenotypes.split('|')"
                :key="$index"
              >
                {{ phenotype }}
              </li>
            </ul>
          </td>
        </tr>
        <tr></tr>
        <tr>
          <td>FAMILY HISTORY</td>
          <td>{{ dataRenalCysticTest.familyHx }}</td>
        </tr>
        <tr></tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataRenalCysticTest.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataRenalCysticTest.quotedPrice }}</td>
        </tr>
      </table>

      <div
        v-if="
          dataRenalCysticTest.family && dataRenalCysticTest.family.length > 0
        "
      >
        <h2 class="mt-8 mb-4">Parent details</h2>
        <table
          class="full-width-table"
          v-for="person in dataRenalCysticTest.family"
          :key="person.personType"
        >
          <tr>
            <td>
              <h3 class="mt-4">{{ person.personType }}</h3>
            </td>
            <td></td>
          </tr>
          <tr>
            <td>PATIENT ID</td>
            <td>{{ person.externalId }}</td>
          </tr>
          <tr>
            <td>AFFECTED BY CONDITION?</td>
            <td>
              {{ keyLookup("yesNoUnknown", person.familyMemberAffected) }}
            </td>
          </tr>
          <tr>
            <td>NAME</td>
            <td>{{ person.firstName }} {{ person.lastName }}</td>
          </tr>
          <tr>
            <td>DATE OF BIRTH</td>
            <td>{{ reformatDob(person.birthDate) }}</td>
          </tr>
          <tr>
            <td>ETHNICITY</td>
            <td>{{ keyLookup("ethnicities", person.ethnicity) }}</td>
          </tr>
          <tr>
            <td>SAMPLE TYPE</td>
            <td>
              {{ sampleTypeName(person.sampleType, true) }}
            </td>
          </tr>
          <tr
            v-if="
              person.sampleType === 'STORED_DNA' ||
                person.sampleType === 'STORED_DATA' ||
                person.sampleType === 'NON_VCGS_SAMPLE'
            "
          >
            <td>SAMPLE ID</td>
            <td>
              {{
                person.sampleType === "STORED_DNA" ||
                person.sampleType === "STORED_DATA"
                  ? person.storedSampleId
                  : person.nonVCGSSampleID
              }}
            </td>
          </tr>
          <tr>
            <td>EMAIL</td>
            <td>{{ person.emailAddress }}</td>
          </tr>
          <tr>
            <td>MOBILE</td>
            <td>{{ person.mobileNum }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td>ADDRESS:</td>
            <td>{{ person.unitFlat }} {{ person.streetAddress }}</td>
          </tr>
          <tr v-if="person.useDifferentAddress === true">
            <td></td>
            <td>
              {{ person.city }} {{ person.state }} {{ person.postcode }}
              {{ keyLookup("countries", person.country) }}
            </td>
          </tr>
          <tr v-else>
            <td>ADDRESS</td>
            <td>Same address as patient</td>
          </tr>
        </table>
      </div>
      <table
        v-if="showRenalCysticTest"
        data-testid="completed-renal-cystic-consent-table"
        class="mb-4"
      >
        <tr>
          <td><h2 class="mt-8 mb-2">Consent</h2></td>
          <td></td>
        </tr>
        <tr>
          <td>PATIENT CONSENT</td>
          <td>{{ yesNoNull(dataRenalCysticTest.patientTestConsentAck) }}</td>
        </tr>
        <tr>
          <td>PATIENT RESEARCH CONSENT</td>
          <td>
            {{ yesNoNull(dataRenalCysticTest.patientResearchConsentAck) }}
          </td>
        </tr>
        <tr></tr>
        <table
          v-if="
            dataRenalCysticTest.family && dataRenalCysticTest.family.length > 0
          "
        >
          <tr>
            <td>PARENT TRIO TEST CONSENT</td>
            <td>{{ yesNoNull(dataRenalCysticTest.trioTestConsentAck) }}</td>
          </tr>
          <tr>
            <td>MOTHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataRenalCysticTest.motherResearchConsentAck) }}
            </td>
          </tr>
          <tr>
            <td>FATHER RESEARCH CONSENT</td>
            <td>
              {{ yesNoNull(dataRenalCysticTest.fatherResearchConsentAck) }}
            </td>
          </tr>
        </table>
      </table>

      <table v-if="showSCA15Test" data-testid="completed-SCA15-table">
        <tr>
          <td><h3>SCA 15</h3></td>
          <td></td>
        </tr>
        <tr>
          <td>FAMILY MEMBER PREVIOUSLY TESTED?</td>
          <td>{{ yesNoNull(dataSCA15Test.familyPrevTested) }}</td>
        </tr>
        <tr>
          <td>VCGS SAMPLE ID</td>
          <td>{{ dataSCA15Test.posCtrlSampleId }}</td>
        </tr>
        <tr>
          <td>RELATION AFFECTED?</td>
          <td>
            {{ dataSCA15Test.relationAffected }}
          </td>
        </tr>
        <tr>
          <td>CLINICAL NOTES</td>
          <td>{{ dataSCA15Test.clinicalNotes }}</td>
        </tr>
        <tr v-if="!showReanalysisAlert">
          <td>PRICE</td>
          <td>{{ dataSCA15Test.quotedPrice }}</td>
        </tr>
      </table>

      <div v-if="showBillingDetails">
        <h2 class="mt-8 mb-2">Billing details</h2>
        <table
          class="full-width-table mb-4"
          data-testid="completed-billing-table"
        >
          <tr>
            <td>INVOICE TO</td>
            <td>
              {{ invoiceTo }}
            </td>
          </tr>
          <tr v-if="showBillingAddress">
            <td>{{ addressTitle }}</td>
            <td>
              {{ invoiceAddress }}
            </td>
          </tr>
        </table>

        <TotalPriceAlert
          :showReanalysisAlert="showReanalysisAlert"
          :showVariantReviewAlert="showVariantReviewAlert"
          summaryScreen
        />
      </div>
    </div>
    <PersistentDialog
      :closeDialog="closeDialog"
      :showdialogflag="showDialogFlag"
      dialogTitle="Please download the order summary/pathology collection forms"
      dialogText="Please download forms before proceeding, as they will be inaccessible upon leaving this page or closing the browser."
    />

    <UploadAlert
      :failedPrevReportFiles="failedPrevReportFiles"
      :failedConsentFiles="failedConsentFiles"
      :closeUploadDialog="closeUploadDialog"
      @retryUpload="retryUpload"
      :uploadTries="uploadTries"
      :maxTries="maxTries"
      :uploadingStatus="uploadingStatus"
      :completeUpload="completeUpload"
    />
  </div>
</template>

<script>
import dropdownsData from "../../public/json/dropdowns.json";
import DropDownMixin from "../mixins/DropDownMixin";
import UtiliseMixin from "../mixins/UtiliseMixin";
import { EventBus } from "../event-bus.js";
import PersistentDialog from "./PersistentDialog.vue";
import UploadAlert from "./upload/UploadAlert.vue";
import TotalPriceAlert from "./common/TotalPriceAlert.vue";
import axios from "axios";
import { useCompletedDetails, useSectionStore } from "../store/SectionStore";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import * as htmlToImage from "html-to-image";
import { computed } from "vue";

const MAX_TRIES = 2;

export default {
  name: "",
  mixins: [DropDownMixin, UtiliseMixin],
  components: {
    PersistentDialog,
    UploadAlert,
    TotalPriceAlert,
  },
  props: {
    formdata: Object,
    appAllPrevReportFiles: Array,
    appAllConsentFiles: Array,
    showReanalysisAlert: Boolean,
    showVariantReviewAlert: Boolean,
  },
  created() {
    window.onbeforeunload = (e) => {
      if (!this.areAllRequiredPDFsDownloaded()) {
        this.showDialogFlag = true;
        e.preventDefault();
        e.returnValue = "";
      }
      return undefined;
    };
  },

  async mounted() {
    const store = useCompletedDetails();
    this.completedStoreData = store;

    this.keyValueLists = this.prepareLists(dropdownsData);

    for (let test of this.formdata.tests) {
      switch (test.testType) {
        case "prader_willi":
          this.showPraderWilli = true;
          this.dataPraderWilli = test;
          break;
        case "microarray":
          this.showMicroArrayTest = true;
          this.dataMicroArrayTest = test;
          break;
        case "fragilex":
          this.showFragileXTest = true;
          this.dataFragileXTest = test;
          break;
        case "variant_detection":
          this.showVariantDetection = true;
          this.dataVariantDetection = test;
          break;
        case "snhl":
          this.showSensorineuralTest = true;
          this.dataSensorineuralTest = test;
          break;
        case "clinical_exome":
          this.showClinexTest = true;
          this.dataClinexTest = test;
          break;
        case "childhood_syndromes":
          this.showChildhoodSyndromes = true;
          this.dataChildhoodSyndromes = test;

          this.getChildhoodTestFamHxParentsAvail(test);
          break;
        case "whole_genome_sequencing":
          this.showWholeGenomeSequencingTest = true;
          this.dataWholeGenomeSequencingTest = test;
          break;
        case "alport":
          this.showAlportTest = true;
          this.dataAlportTest = test;
          break;
        case "renal_non_cystic":
          this.showRenalNonCysticTest = true;
          this.dataRenalNonCysticTest = test;
          break;
        case "renal_cystic":
          this.showRenalCysticTest = true;
          this.dataRenalCysticTest = test;
          break;
        case "SCA15":
          this.showSCA15Test = true;
          this.dataSCA15Test = test;
          break;
        case "connexin_dfnb1":
          this.showConnexinTest = true;
          this.dataConnexinTest = test;
          break;
        case "deafness_mlpa":
          this.mediumTestData.deafness.show = true;
          this.mediumTestData.deafness.data = test;
          break;
        case "PHOX2B":
          this.mediumTestData.phox2b.show = true;
          this.mediumTestData.phox2b.data = test;
          break;
        case "mito":
          this.showMitoTest = true;
          this.dataMitoTest = test;
          break;
        case "mito_cascade":
          this.showMitoCascadeTest = true;
          this.dataMitoCascadeTest = test;
          break;
        case "mito_diagnostic":
          this.showMitoDiseaseTest = true;
          this.dataMitoDiseaseTest = test;
          break;
        case "prenatal_targeted_exome":
          this.dataPrenatalTargetedExome = store.prenatal_targeted_exome;
          this.showPrenatalTargetedExome = true;
          break;
        case "prenatal_targeted_genome":
          this.dataPrenatalTargetedWGS = store.prenatal_targeted_genome;
          this.showPrenatalTargetedWGS = true;
          break;
        case "prenatal_rapid_genomic_trio":
          this.dataPrenatalRapidGenomicTrio = store.prenatal_rapid_genomic_trio;
          this.showPrenatalRapidGenomicTrio = true;
      }
    }

    this.consentFiles = this.appAllConsentFiles;
    this.prevReportFiles = this.appAllPrevReportFiles;

    await this.uploadAllFiles();

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  },
  computed: {
    reformattedPatientDob() {
      return this.reformatDob(this.formdata.patient.birthDate);
    },
    invoiceTo() {
      if (
        this.formdata.totalDisplayPrice === "$0" &&
        useSectionStore().freeTestForVictorian
      ) {
        //only invoice to DHHS if there are no other tests with a cost
        return "DH (formerly DHHS)";
      }
      return this.formdata.billing.sendBillTo !== "OTHER"
        ? this.sendBillToName(this.formdata.billing.sendBillTo)
        : this.formdata.billing.billingName;
    },
    invoiceAddress: function() {
      let billingAddress = "";
      switch (this.formdata.billing.sendBillTo) {
        case "OTHER":
          billingAddress = this.formdata.billing.billingAddress;
          break;
        case "VCGS":
          billingAddress = this.formdata.billing.billingName;
          break;
        case "PATIENT":
          billingAddress = "Patient's address";
          break;
        default:
          break;
      }
      return billingAddress;
    },
    showBillingDetails: function() {
      return (
        this.formdata.billing.sendBillTo !== "" &&
        this.formdata.billing.sendBillTo !== null
      );
    },
    showBillingAddress: function() {
      return this.formdata.billing.sendBillTo !== "MEDICARE";
    },
    addressTitle: function() {
      let addressTitle = "";
      switch (this.formdata.billing.sendBillTo) {
        case "VCGS":
          addressTitle = "AUTHORISED BY";
          break;
        default:
          addressTitle = "ADDRESS";
          break;
      }
      return addressTitle;
    },
  },
  data: () => ({
    completedStoreData: {},
    showPrenatalExomeFocused: false,
    showPrenatalRapidGenomicTrio: false,
    dataPrenatalRapidGenomicTrio: {},
    showPrenatalTargetedExome: false,
    dataPrenatalTargetedExome: {},
    showPrenatalTargetedWGS: false,
    dataPrenatalTargetedWGS: {},
    showPraderWilli: false,
    dataPraderWilli: {},
    showFragileXTest: false,
    dataFragileXTest: {},
    showMicroArrayTest: false,
    dataMicroArrayTest: {},
    showVariantDetection: false,
    dataVariantDetection: {},
    showClinexTest: false,
    dataClinexTest: {},
    showChildhoodSyndromes: false,
    dataChildhoodSyndromes: {},
    showWholeGenomeSequencingTest: false,
    dataWholeGenomeSequencingTest: {},
    showAlportTest: false,
    dataAlportTest: {},
    showRenalNonCysticTest: false,
    dataRenalNonCysticTest: {},
    showRenalCysticTest: false,
    dataRenalCysticTest: {},
    showSCA15Test: false,
    dataSCA15Test: {},
    showSensorineuralTest: false,
    dataSensorineuralTest: {},
    showConnexinTest: false,
    dataConnexinTest: {},
    mediumTestData: {
      deafness: {
        show: false,
        data: {},
      },
      phox2b: {
        testName: "PHOX2B poly-alanine expansion testing",
        show: false,
        data: {},
      },
    },
    showMitoTest: false,
    dataMitoTest: {},
    showMitoCascadeTest: false,
    dataMitoCascadeTest: {},
    showMitoDiseaseTest: false,
    dataMitoDiseaseTest: {},
    keyValueLists: {},
    motherData: {},
    fatherData: {},
    vcgsUseOnly: null,
    copyReportsTo: null,
    requestingDoctor: null,
    family: ["mother", "father", "patient"],
    downloadedFiles: {
      patient: false,
      mother: false,
      father: false,
      screenshot: false,
    },
    showDialogFlag: false,
    prevReportFiles: [],
    consentFiles: [],
    failedPrevReportFiles: [],
    failedConsentFiles: [],
    maxTries: MAX_TRIES,
    uploadTries: 0,
    uploadingStatus: false,
    completeUpload: null,
    previousReportUploadId: 1,
    consentUploadId: 1,
    childhoodFamHx: "",
    childhoodParentsAvail: "",
  }),
  setup() {
    const store = useSectionStore();
    const pregnant = computed(() => store.pregnant);

    return { pregnant, store };
  },
  methods: {
    reformatDob(dob) {
      // Check if dob is null or an empty string
      if (dob === null || dob.length === 0) return "";

      // Split the date string into [YYYY, MM, DD]
      const parts = dob.split("-");

      // Rearrange the parts into [DD, MM, YYYY]
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },
    getChildhoodTestFamHxParentsAvail(test) {
      /** As the data of both parents availability and family history fields are concatenated to familyHx
       * in ChildhoodSyndromes test, we extract the responsive data here to populate the relevant td in the table
       */

      const concatenatedStr = test.familyHx;
      // When neith parents availability nor family history fields have input
      if (concatenatedStr === "") {
        this.childhoodParentsAvail = "";
        this.childhoodFamHx = "";

        return;
      }

      // When only family history has data input
      if (!concatenatedStr.includes("Biological parents availability")) {
        this.childhoodParentsAvail = "";
        this.childhoodFamHx = concatenatedStr;
        return;
      }

      const splitted = concatenatedStr.split("\n");
      const parentsAvailSubstr = splitted[0];
      const famHxSubstr = splitted[1];

      /** Handle two situations:
       * 1) Only parents availability data
       * 2) both parents availablity and family history data
       */
      this.childhoodParentsAvail = parentsAvailSubstr.replace(
        "Biological parents availability: ",
        "",
      );

      if (famHxSubstr) this.childhoodFamHx = famHxSubstr;
    },
    async retryUpload({ retryConsentFiles, retryPrevReportFiles }) {
      this.prevReportFiles = retryPrevReportFiles;
      this.consentFiles = retryConsentFiles;

      await this.uploadAllFiles();
    },
    addError(index, errorObj, uploadType) {
      let uploadFilesName;
      switch (uploadType) {
        case "previousReport":
          uploadFilesName = "prevReportFiles";
          break;
        case "consent":
          uploadFilesName = "consentFiles";
          break;
      }

      if (this[uploadFilesName][index].errors)
        return (this[uploadFilesName][index] = {
          ...this[uploadFilesName][index],
          errors: [...this[uploadFilesName][index].errors, errorObj],
        });

      return (this[uploadFilesName][index] = {
        ...this[uploadFilesName][index],
        errors: [errorObj],
      });
    },
    checkUploadErrors(uploadType) {
      let filesFailedUpload = 0;

      let uploadFilesName;
      let failedFilesName;
      switch (uploadType) {
        case "previousReport":
          uploadFilesName = "prevReportFiles";
          failedFilesName = "failedPrevReportFiles";
          break;
        case "consent":
          uploadFilesName = "consentFiles";
          failedFilesName = "failedConsentFiles";
          break;
      }

      for (let i = 0; i < this[uploadFilesName].length; i++) {
        if (
          this[uploadFilesName][i].errors &&
          this[uploadFilesName][i].errors.length > 0
        ) {
          this[failedFilesName] = [
            ...this[failedFilesName],
            this[uploadFilesName][i],
          ];

          filesFailedUpload++;
        }
      }

      return filesFailedUpload;
    },
    checkAllUploadErrors() {
      this.failedPrevReportFiles = [];
      this.failedConsentFiles = [];

      const failedPrevReportFilesNum = this.checkUploadErrors("previousReport");
      const failedConsentFilesNum = this.checkUploadErrors("consent");

      this.completeUpload =
        failedConsentFilesNum + failedPrevReportFilesNum === 0 ? true : false;
    },
    async uploadFiles(filesToUpload, uploadType) {
      const uploadIdName = `${uploadType}UploadId`;

      for (let i = 0; i < filesToUpload.length; i++) {
        const uploadObj = filesToUpload[i];

        for (let j = 0; j < uploadObj.files.length; j++) {
          const file = uploadObj.files[j];

          const getSignedS3UploadURLResult = await this.getSignedS3UploadURL(
            file,
            uploadType,
            this.formdata.orderId,
            this[uploadIdName].toString(),
          );

          if (getSignedS3UploadURLResult.success) {
            const uploadFileToS3Result = await this.uploadFileToS3(
              file,
              getSignedS3UploadURLResult.signedS3UploadURL,
            );

            if (uploadFileToS3Result.success) {
              this[uploadIdName]++;
            } else {
              this.addError(
                i,
                {
                  name: file.name,
                  error: uploadFileToS3Result,
                },
                uploadType,
              );
            }
          } else {
            this.addError(
              i,
              {
                name: file.name,
                error: getSignedS3UploadURLResult,
              },
              uploadType,
            );
          }
        }
      }
    },
    async uploadAllFiles() {
      if (this.consentFiles.length === 0 && this.prevReportFiles.length === 0)
        return;

      if (this.uploadTries >= this.maxTries) return;

      this.uploadTries++;

      this.completeUpload = false;

      this.uploadingStatus = true;

      // Upload previous report files
      await this.uploadFiles(this.prevReportFiles, "previousReport");

      // Upload consent files
      await this.uploadFiles(this.consentFiles, "consent");

      this.uploadingStatus = false;

      this.checkAllUploadErrors();
    },
    /*convert 'yyyy-mm-dd' to 'dd/mm/yyyy'*/
    convertDate(date) {
      if (!date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        throw new Error("Expected date format yyyy-mm-dd");
      }
      let split = date.split("-");
      return split[2] + "/" + split[1] + "/" + split[0];
    },
    showDialog() {
      this.showDialogFlag = true;
    },
    closeDialog() {
      this.showDialogFlag = false;
    },
    closeUploadDialog() {
      this.completeUpload = null;
    },
    getPersonTypeData(personType) {
      const testsWithTrio = this.formdata.tests.filter(
        (test) =>
          test.testType === "clinical_exome" ||
          test.testType === "childhood_syndromes" ||
          test.testType === "whole_genome_sequencing" ||
          test.testType === "mito_diagnostic" ||
          test.testType === "variant_detection" ||
          test.testType === "prenatal_targeted_exome" ||
          test.testType === "prenatal_rapid_genomic_trio" ||
          (test.testType === "prenatal_targeted_genome" &&
            // we removed familyType for prenatal tests so just check the family array
            (test.familyType === "Trio" || test.family.length === 2)),
      );

      // To prevent returning an `undefined`
      if (!testsWithTrio.length) return [];

      // We'll be picking the first element of the possible Trio test array as we only support storing one mother/ father data pair
      // down the flow ATM.
      return testsWithTrio[0].family.filter(
        (member) => member.personType === personType.toUpperCase(),
      ); // mother -> MOTHER, father -> FATHER
    },

    async sendPDFData(personType) {
      let collectionForm,
        patientDetails,
        testsRequested,
        hospitalStatus,
        clinicalInformation,
        readableAddress;

      this.motherData = this.getPersonTypeData("mother");
      this.fatherData = this.getPersonTypeData("father");

      /* The next three fields are 'common' between the three types of patients, thus
       they shall be generated and saved as global variables */
      if (!this.vcgsUseOnly) {
        // dateSubmitted is ISO8601 so split on space will return YYYY-MM-DD
        let date = this.formdata.dateSubmitted.split(" ");
        this.vcgsUseOnly = {
          orderId: this.formdata.orderId,
          // need to convert yyyy-mm-dd to dd/mm/yyyy
          orderDate: this.convertDate(date[0]),
        };
      }

      if (!this.copyReportsTo) {
        let listOfDoctors = this.formdata.practitioner.copyToDoctors;
        this.copyReportsTo = listOfDoctors.map((doctor) => {
          return {
            providerNum: doctor.providerNum,
            name: doctor.firstName + " " + doctor.lastName,
            clinic: doctor.clinicName,
          };
        });
      }

      if (!this.requestingDoctor) {
        let {
          providerNum,
          firstName,
          lastName,
          namePrefix,
          clinicName,
          dept,
          unitFlat,
          streetAddress,
          city,
          state,
          postcode,
          country,
        } = this.formdata.practitioner;

        let addressFields = [
          clinicName,
          dept,
          unitFlat,
          streetAddress,
          city,
          state,
          postcode,
          country,
        ];
        let address = "";

        for (const x in addressFields) {
          if ((addressFields[x] || "").length > 0) {
            if (x == addressFields.length - 1) {
              address = address.concat(addressFields[x]);
            } else {
              address = address.concat(addressFields[x] + ", ");
            }
          }
        }

        this.requestingDoctor = {
          providerNum,
          name: namePrefix + " " + firstName + " " + lastName,
          address,
        };
      }

      /* retrieve the patient specific data based on personType( ie. patient, mother, father) so fields can be filled
      in below */
      if (
        personType === "patient" &&
        Object.keys(this.formdata.patient).length
      ) {
        var {
          firstName,
          lastName,
          sex,
          birthDate,
          medicareNum,
          mediPersonNum,
          mobileNum,
          postcode,
          unitFlat,
          streetAddress,
          city,
          state,
          sampleType,
          nonVCGSSampleID,
        } = this.formdata.patient;
      } else if (
        personType === "mother" &&
        Object.keys(this.motherData[0]).length
      ) {
        var {
          firstName,
          lastName,
          sex,
          birthDate,
          mobileNum,
          postcode,
          unitFlat,
          streetAddress,
          city,
          state,
          sampleType,
          nonVCGSSampleID,
        } = this.motherData[0];
      } else if (
        personType === "father" &&
        Object.keys(this.fatherData[0]).length
      ) {
        var {
          firstName,
          lastName,
          sex,
          birthDate,
          mobileNum,
          postcode,
          unitFlat,
          streetAddress,
          city,
          state,
          sampleType,
          nonVCGSSampleID,
        } = this.fatherData[0];
      }

      if (birthDate) {
        // patient birthdate fields are formatted yyyy-mm-dd
        // need to convert yyyy-mm-dd to dd/mm/yyyy
        birthDate = this.convertDate(birthDate);
      }

      /* if address fields are not provided - personType is parent and assume that the address is the same as the proband */
      if (!unitFlat && !streetAddress && !city && !state) {
        readableAddress =
          this.formdata.patient.unitFlat ||
          "" +
            " " +
            this.formdata.patient.streetAddress +
            ", " +
            this.formdata.patient.city +
            " " +
            this.formdata.patient.state;
        postcode = this.formdata.patient.postcode;
        mobileNum = this.formdata.patient.mobileNum;
      } else {
        readableAddress =
          (unitFlat || "") + " " + streetAddress + ", " + city + " " + state;
      }

      /* Fill in patient details */
      patientDetails = {
        givenNames: firstName,
        lastName,
        sex: sex,
        dateOfBirth: birthDate,
        address: readableAddress,
        postCode: postcode,
        mobile: mobileNum,
        medicareNumber:
          personType === "patient" ? `${medicareNum}/${mediPersonNum}` : "",
      };

      // Filters through selected tests to only include trio tests
      const filterTrioTestsRequested = (tests) => {
        return tests
          .filter((test) => test.family && test.family.length === 2)
          .map((test) => test.testType);
      };

      if (this.store.isPrenatal) {
        /** When it is prenatal order, the patient and the mother have the same tests requested
         */
        if (personType === "patient" || personType === "mother") {
          testsRequested = this.formdata.tests.map((test) => test.testType);
        }

        /** When it is prenatal order, father's pathology form only includes trio tests as tests requested*/
        if (personType === "father") {
          testsRequested = filterTrioTestsRequested(this.formdata.tests);
        }
      } else {
        if (personType === "patient") {
          testsRequested = this.formdata.tests.map((test) => test.testType);
        }

        /** When it is postnatal order, the mother's and father's pathology forms only include trio tests as tests requested */
        if (personType === "mother" || personType === "father") {
          testsRequested = filterTrioTestsRequested(this.formdata.tests);
        }
      }

      if (sampleType === "BLOOD") {
        const formDataTests = this.formdata.tests;

        if (
          formDataTests.length === 1 &&
          formDataTests[0].testType === "variant_detection" &&
          formDataTests[0].predictiveTest
        ) {
          // Assign BLOOD1 if the order only includes variant_detection test and its predictiveTest is true
          sampleType = "BLOOD1";
        } else if (formDataTests.some((test) => test.predictiveTest)) {
          // Assign BLOOD2
          // 1) if the order includes variant_detection and other tests and at least one test with predictiveTest true
          // 2) if the order includes one test (other than variant_detection) or multiple tests and at least one test with predictiveTest true
          sampleType = "BLOOD2";
        } else {
          // Assign BLOOD1 if none of the tests have predictiveTest true
          sampleType = "BLOOD1";
        }
      }

      /* Get hospital status of patient -> match to one of the 4 options */

      if (
        this.formdata.patient.patientStatusContent === "private_private_suite"
      ) {
        hospitalStatus =
          "Private patient in a private suite, private hospital or approved day hospital facility";
      } else if (
        this.formdata.patient.patientStatusContent ===
        "private_recognised_hospital"
      ) {
        hospitalStatus = "Private patient in a recognised hospital";
      } else if (
        this.formdata.patient.patientStatusContent === "public_public_hospital"
      ) {
        hospitalStatus = "Public patient in a recognised public hospital";
      } else if (
        this.formdata.patient.patientStatusContent ===
        "public_outpatient_public_hospital"
      ) {
        hospitalStatus = "Public outpatient of a recognised public hospital";
      }

      /* Construct clinical information */

      clinicalInformation = "Refer to online order";

      /* formation of final packet */
      collectionForm = {
        vcgsUseOnly: this.vcgsUseOnly,
        patientDetails,
        testsRequested,
        sampleType,
        nonVCGSSampleID,
        requestingDoctor: this.requestingDoctor,
        copyReportsTo: this.copyReportsTo,
        hospitalStatus,
        clinicalInformation,
      };

      // only log data packet with PII during dev/test
      if (!this.$dataset.isProductionLike) {
        console.log(JSON.stringify(collectionForm));
      }
      return collectionForm;
    },

    async generatePdf(collectionForm) {
      try {
        const response = await axios.post(
          `${this.$dataset.pdfServiceUrl}/api/pdf/specimen_collection.pdf`,
          collectionForm,
          {
            responseType: "arraybuffer",
          },
        );

        const pdfDoc = response.data;

        //serialize pdfDoc to bytes
        const pdfBytes = await PDFDocument.load(pdfDoc);

        return await pdfBytes.save();
      } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
      }
    },
    applyPrintStyles() {
      // Add class to body or specific element to apply print-specific styles
      document.body.classList.add("print-media");
    },
    resetPrintStyles() {
      //Remove print-media specific class elements after screenshot has been taken
      document.body.classList.remove("print-media");
    },

    getMembersToDownloadSpecimenPDF() {
      return this.family.filter((member) =>
        this.shouldPersonDownloadPDF(member),
      );
    },
    async screenshotAsPNG(zip, folderName) {
      this.applyPrintStyles();

      const pdfContent = this.$refs.screenContent;

      const blob = await htmlToImage.toBlob(pdfContent, {
        backgroundColor: "#fff",
      });

      zip.file(`${folderName}/Order_summary.png`, blob, { binary: true });
    },

    async downloadAllRelevantForms() {
      //generate zip folder
      const zip = new JSZip();

      const folderName = `VCGS_specimen_collection_forms_${this.formdata.patient.firstName}_${this.formdata.patient.lastName}`;
      const folder = zip.folder(folderName);

      try {
        try {
          await this.screenshotAsPNG(zip, folderName);
          this.downloadedFiles.screenshot = true;
        } catch (error) {
          console.log("Error generating screenshot PNG:", error);
          throw error;
        } finally {
          this.resetPrintStyles();
        }

        const membersToDownloadPDF = this.getMembersToDownloadSpecimenPDF();

        if (membersToDownloadPDF.length > 0) {
          for (const member of membersToDownloadPDF) {
            const collectionData = await this.sendPDFData(member);

            const isFetusWithDownload = ["AMNIO", "CVS"].includes(
              collectionData.sampleType,
            );

            /** When it is prenatal test, the mother and the baby have the same details except sampleType.
             * To differentiate the file names for the mother and the baby, we dynamically render `fileName`
             * based on the selected sampleType, e.g.
             * collectionData.patientDetails.givenNames = "foo"
             * collectionData.patientDetails.lastName = "bar"
             * isFetusWithDownload = true
             * `fileName` is "VCGS_specimen_collection_form_foo_bar_Fetus.pdf"
             */
            const fileName = `VCGS_specimen_collection_form_${
              collectionData.patientDetails.givenNames
            }_${collectionData.patientDetails.lastName}${
              isFetusWithDownload ? "_Fetus" : ""
            }.pdf`;

            try {
              const res = await this.generatePdf(collectionData);
              folder.file(fileName, res);
              this.downloadedFiles[member] = true;
            } catch (error) {
              console.log(`error adding ${member} to folder: `, error);
              throw error;
            }
          }
        }

        // Generate the zip file as a Blob
        const zipBlob = await zip.generateAsync({ type: "blob" });

        // Create a URL for the zip Blob
        const zipURL = URL.createObjectURL(zipBlob);

        // Create a download link for the zip file
        const link = document.createElement("a");
        link.href = zipURL;
        link.setAttribute(
          "download",
          `VCGS_submission_receipts_${this.formdata.patient.firstName}_${this.formdata.patient.lastName}`,
        );

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.error("Error downloading forms:", error);
        window.alert(
          "The download failed. Please try again or contact VCGS support.",
        );
      }
    },

    /**
     * Check to see if the pathology collection form alert should be displayed based on sample type
     * @returns {boolean} boolean
     */
    checkToDisplayPathologyAlert() {
      return (
        this.checkForAnySampleType("BLOOD") ||
        this.checkForAnySampleType("SALIVA_NO_KIT") ||
        this.checkForAnySampleType("NON_VCGS_SAMPLE") ||
        this.checkForAnySampleType("AMNIO") ||
        this.checkForAnySampleType("CVS")
      );
    },

    /**
     * Check if any person has particular sample type
     *
     * @param {string} sampleType sample type (one of "BLOOD", "SALIVA", "SALIVA_NO_KIT")
     * @returns {boolean} boolean
     */
    checkForAnySampleType(sampleType) {
      const personTypes = ["patient", "mother", "father"];

      return personTypes.some((personType) =>
        this.checkForSampleType(personType, sampleType),
      );
    },

    /**
     * Get the sample type of a particular person
     *
     * @param {string} personType person type (one of "patient", "mother" or "father")
     * @returns {string | undefined} sampleType (if it exists)
     */
    getSampleType(personType) {
      const personData =
        personType === "patient"
          ? this.formdata.patient
          : this.getPersonTypeData(personType)[0];

      return personData ? personData.sampleType : undefined;
    },

    /**
     * Check if a sample type matches for a person type
     *
     * @param {string} personType person type (one of "patient", "mother" or "father")
     * @param {string} sampleType sample type (one of "BLOOD", "SALIVA", "SALIVA_NO_KIT")
     * @returns {boolean} boolean
     */
    checkForSampleType(personType, sampleType) {
      return this.getSampleType(personType) === sampleType;
    },

    /**
     * Check if a person has a sample type that requires a PDF download, depending on their sample type
     *
     * @param {string} personType person type (one of "patient", "mother" or "father")
     * @returns True if the person requires a PDF download, false otherwise
     */
    shouldPersonDownloadPDF(personType) {
      return [
        "BLOOD",
        "SALIVA_NO_KIT",
        "NON_VCGS_SAMPLE",
        "AMNIO",
        "CVS",
      ].includes(this.getSampleType(personType));
    },

    /**
     * Checks if all required PDFs have been downloaded
     *
     * @returns True if they've all been downloaded, false otherwise.
     */
    areAllRequiredPDFsDownloaded() {
      return ["patient", "mother", "father", "screenshot"].reduce(
        (acc, personType) => {
          return (
            acc &&
            (!this.shouldPersonDownloadPDF(personType) ||
              this.downloadedFiles[personType])
          );
        },
        true,
      );
    },

    yesNoNull(value) {
      switch (value) {
        case true:
          return "Yes";
        case false:
          return "No";
        case null:
          return "";
        default:
          return value;
      }
    },
    reloadPage() {
      window.location.reload();
    },
    orderForFamilyMember() {
      useSectionStore().removeAllTests();

      let reuseData = {
        reusePractitionerData: this.formdata.practitioner,
        reusePatientData: this.formdata.patient,
      };
      EventBus.$emit("reusePractitionerAndPatientDetails", reuseData);
    },
    prepareLists(lists) {
      let fixedLists = {};
      if (lists) {
        for (let category of Object.keys(lists)) {
          fixedLists[category] = {};
          for (let item of lists[category]) {
            fixedLists[category][item["value"]] = item["displayText"];
          }
        }
      }
      return fixedLists;
    },
    keyLookup(category, key) {
      if (this.keyValueLists[category] && this.keyValueLists[category][key]) {
        return this.keyValueLists[category][key];
      } else {
        return key;
      }
    },
  },
};
</script>

<style scoped>
#header-grid {
  display: flex;
  align-items: center;
  padding: 20px;
  justify-content: space-between;
}
.item {
  padding: 35px;
}
.no-style-list {
  list-style: none;
  padding-left: 0;
}
td:first-child {
  width: 250px;
}
table {
  table-layout: fixed;
  width: 100%;
}
.buttons-container {
  margin-bottom: 20px;
  display: flex;
}
@media print {
  .hidden-print-only {
    display: none !important;
  }
}

/* Additional styles for print media */
.print-media .hidden-print-only {
  display: none !important;
}
</style>
